/**
 * EmployeeMultiSelect — pick one or more employees for shared file notes.
 */
import { useMemo } from 'react';
import { X } from 'lucide-react';
import { Field } from '../../../shell/Field.js';
import { Button } from '../../../shell/Button.js';
import { useEmployeeDirectory } from '../../employees/api.js';
import { EmployeeSelect, formatEmployeeOption } from '../../employees/components/EmployeeSelect.js';

interface Props {
  label?: string;
  value: string[];
  onChange: (employeeIds: string[]) => void;
  /** Pre-selected employee that cannot be removed (e.g. current profile). */
  pinnedId?: string;
  excludeIds?: string[];
  error?: string;
}

export function EmployeeMultiSelect({
  label = 'Employees on file',
  value,
  onChange,
  pinnedId,
  excludeIds = [],
  error,
}: Props) {
  const { data: directory } = useEmployeeDirectory();
  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of directory?.items ?? []) {
      map.set(e.id, formatEmployeeOption(e));
    }
    return map;
  }, [directory]);

  const selectedSet = new Set(value);
  const availableToAdd = value.length === 0 ? '' : '';

  const addEmployee = (id: string) => {
    if (!id || selectedSet.has(id)) return;
    onChange([...value, id]);
  };

  const removeEmployee = (id: string) => {
    if (id === pinnedId) return;
    onChange(value.filter((v) => v !== id));
  };

  return (
    <Field label={label} error={error}>
      <div className="space-y-2">
        {value.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {value.map((id) => (
              <li
                key={id}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-sm dark:border-slate-700 dark:bg-slate-800"
              >
                <span className="max-w-[220px] truncate text-slate-800 dark:text-slate-200">
                  {labelById.get(id) ?? 'Employee'}
                </span>
                {id !== pinnedId && (
                  <button
                    type="button"
                    className="rounded p-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    aria-label={`Remove ${labelById.get(id) ?? 'employee'}`}
                    onClick={() => removeEmployee(id)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">Select at least one employee.</p>
        )}

        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <EmployeeSelect
              label="Add employee"
              value={availableToAdd}
              onChange={addEmployee}
              excludeIds={[...excludeIds, ...value]}
              directory
              placeholder="Add another employee…"
            />
          </div>
          {value.length > 1 && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => onChange(pinnedId ? [pinnedId] : value.slice(0, 1))}
            >
              Clear extras
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          The same note will appear on each selected employee&apos;s personnel file.
        </p>
      </div>
    </Field>
  );
}
